import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { studentAPI } from '../../services/api';
import { DocumentSecurity } from '../../services/documentSecurity';
import Loading from '../common/Loading';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const AdministrativeSituation = () => {
  const [situationData, setSituationData] = useState(null);
  const [statsData, setStatsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();


  const fetchAdministrativeSituation = async (year = '') => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [situationResponse, statsResponse] = await Promise.all([
        studentAPI.getAdministrativeSituation({ year }),
        studentAPI.getAdministrativeStats()
      ]);
      
      setSituationData(situationResponse.administrative_situation);
      setStatsData(statsResponse.statistics);
      setAvailableYears(situationResponse.available_years);
      
      // Set default year to the most recent
      if (!year && situationResponse.available_years.length > 0) {
        setSelectedYear(situationResponse.available_years[0].toString());
      }
      
    } catch (err) {
      setError(err.response?.data?.error || t('as_errorLoad'));
      console.error('Error fetching administrative situation:', err);
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    fetchAdministrativeSituation();
  }, []);

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    fetchAdministrativeSituation(year);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-MA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };


  // Add this function to AdministrativeSituation component
const generateAdministrativeSituationPDF = async (year, registrations) => {
  try {
    const jsPDF = (await import('jspdf')).default;
    await import('jspdf-autotable');

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Load Arabic font
    try {
      const fontResponse = await fetch('/fonts/Amiri-Regular.ttf');
      if (fontResponse.ok) {
        const fontBlob = await fontResponse.blob();
        const reader = new FileReader();
        const fontBase64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(fontBlob);
        });
        doc.addFileToVFS('Amiri-Regular.ttf', fontBase64);
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
      }
    } catch (fontError) {
      console.error("Font loading failed:", fontError);
    }

    doc.setFont('Amiri', 'normal');

    const primaryColor = [44, 62, 80];
    const textColor = [33, 37, 41];

    // Header
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('Amiri', 'bold');
    doc.text('SITUATION ADMINISTRATIVE', pageWidth / 2, 15, { align: 'center' });
    doc.setFont('Amiri', 'normal');
    doc.setFontSize(11);
    doc.text('الوضعية الإدارية', pageWidth / 2, 22, { align: 'center' });
    doc.text('UNIVERSITÉ HASSAN 1ER - SETTAT', pageWidth / 2, 30, { align: 'center' });

    // Title
    doc.setTextColor(...textColor);
    doc.setFontSize(14);
    doc.text(`Année Universitaire ${year} - ${parseInt(year) + 1}`, pageWidth / 2, 50, { align: 'center' });

    // Student info table
    doc.autoTable({
      body: [
        [`Nom complet:`, `${user?.nom_complet || 'N/A'}`],
        [`Code étudiant:`, `${user?.cod_etu || 'N/A'}`],
        [`CIN:`, `${user?.cin || 'N/A'}`],
        [`Année:`, `${year}`],
      ],
      startY: 60,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1.5, font: 'Amiri' },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // Registrations table
    const tableData = registrations.map(reg => [
      (reg.lib_etp || reg.lic_etp || 'N/A').substring(0, 50),
      reg.cod_etp,
      reg.eta_iae === 'E' ? 'Active' : 'Inactive',
      reg.tem_iae_prm === 'O' ? 'Oui' : 'Non',
      reg.dat_cre_iae ? new Date(reg.dat_cre_iae).toLocaleDateString('fr-FR') : 'N/A'
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Spécialité', 'Code', 'État', 'Principal', 'Date Création']],
      body: tableData,
      theme: 'grid',
      styles: { 
        font: 'Amiri', 
        fontSize: 8.5, 
        cellPadding: 2.5,
        textColor: textColor,
        lineColor: [220, 220, 220],
        lineWidth: 0.2
      },
      headStyles: { 
        fillColor: primaryColor, 
        textColor: 255, 
        fontStyle: 'bold', 
        fontSize: 9,
        halign: 'center'
      }
    });

    // *** THIS IS THE KEY PART - EXACTLY LIKE OfficialDocuments ***
    const documentData = {
      studentId: user?.cod_etu,
      semester: `ADMIN_${year}`,
      documentType: 'administrative_situation',
      year: year,
      timestamp: new Date().toISOString(),
      registrations: registrations.map(r => ({
        cod_etp: r.cod_etp,
        lib_etp: r.lib_etp,
        eta_iae: r.eta_iae
      }))
    };

    const { signature, qrCodeDataUrl } = await DocumentSecurity.signDocument(documentData);

    // Add QR code
    if (qrCodeDataUrl) {
      try {
        const qrCodeImage = qrCodeDataUrl.split(',')[1];
        doc.addImage(qrCodeImage, 'PNG', pageWidth - 40, pageHeight - 50, 30, 30);
      } catch (qrError) {
        console.error('Failed to add QR code:', qrError);
      }
    }

    // Add verification info
    doc.setFont('Amiri', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100);

    const documentId = `ADMIN-${user?.cod_etu}-${year}-${Date.now()}`;
    doc.text(`ID Document: ${documentId}`, 15, pageHeight - 35);
    doc.text(`Signature numérique: ${signature}`, 15, pageHeight - 30);
    doc.text(`Vérification: Scannez le QR code`, 15, pageHeight - 25);

    // Footer
    doc.setTextColor(150);
    doc.text(`Généré le ${new Date().toLocaleString('fr-FR')} | Certifié par le système académique`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    return doc;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
};

// Add download and print handlers
const handleDownloadPDF = async (year, registrations) => {
  try {
    const doc = await generateAdministrativeSituationPDF(year, registrations);
    if (doc) {
      doc.save(`Situation_Administrative_${year}_${user?.cod_etu}.pdf`);
    }
  } catch (err) {
    console.error("Error generating PDF:", err);
    setError('Erreur lors de la génération du PDF');
  }
};

const handlePrintPDF = async (year, registrations) => {
  try {
    const doc = await generateAdministrativeSituationPDF(year, registrations);
    if (doc) {
      doc.output('dataurlnewwindow');
    }
  } catch (err) {
    console.error("Error printing PDF:", err);
    setError('Erreur lors de l\'impression du PDF');
  }
};
const generateEnrollmentCertificate = async (year, registrations) => {
  try {
    const jsPDF = (await import('jspdf')).default;
    
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Load Arabic font
    try {
      const fontResponse = await fetch('/fonts/Amiri-Regular.ttf');
      if (fontResponse.ok) {
        const fontBlob = await fontResponse.blob();
        const reader = new FileReader();
        const fontBase64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(fontBlob);
        });
        doc.addFileToVFS('Amiri-Regular.ttf', fontBase64);
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
        doc.setFont('Amiri', 'normal');
      }
    } catch (fontError) {
      console.warn("Font loading failed, using default font:", fontError);
    }

    doc.setTextColor(0, 0, 0);

    // ===== HEADER =====
    doc.setFontSize(10);
    // Left side (French)
    doc.text('ROYAUME DU MAROC', 15, 15);
    doc.text('Université Hassan 1er', 15, 21);
    doc.text('FACULTÉ DES SCIENCES', 15, 27);
    doc.text('JURIDIQUES ET POLITIQUES -', 15, 33);
    doc.text('SETTAT', 15, 39);
    doc.setFontSize(8);
    doc.text('Service des Affaires Estudiantines', 15, 45);

    // Right side (Arabic)
    doc.setFontSize(10);
    doc.text('المملكة المغربية', pageWidth - 15, 15, { align: 'right' });
    doc.text('جامعة الحسن الأول', pageWidth - 15, 21, { align: 'right' });
    doc.text('كلية العلوم القانونية والسياسية', pageWidth - 15, 27, { align: 'right' });
    doc.text('سطات', pageWidth - 15, 33, { align: 'right' });
    doc.setFontSize(8);
    doc.text('مصلحة الشؤون الطلابية', pageWidth - 15, 39, { align: 'right' });

    // ===== TITLE =====
    doc.setFontSize(16);
    doc.setFont('Amiri', 'bold');
    doc.text('ATTESTATION D\'INSCRIPTION', pageWidth / 2, 65, { align: 'center' });

    // ===== MAIN CONTENT =====
    doc.setFont('Amiri', 'normal');
    doc.setFontSize(10);

    doc.text('La Doyenne de la Faculté des Sciences Juridiques et Politiques - Settat atteste que', 20, 82);
    doc.text('l\'étudiant(e) :', 20, 90);

    // Student name
    doc.setFont('Amiri', 'bold');
    doc.text(user?.nom_complet || 'N/A', 20, 100);

    // Student details
    doc.setFont('Amiri', 'normal');
    doc.text(`Numéro de la carte d'identité nationale : ${user?.cin || 'N/A'}`, 20, 110);
    doc.text(`Code national de l'étudiant(e) : ${user?.cod_nne_ind || user?.cod_etu || 'N/A'}`, 20, 118);
    
    // Birth info
    let birthText = 'né(e) le ';
    if (user?.date_naissance) {
      const birthDate = new Date(user.date_naissance);
      birthText += birthDate.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } else {
      birthText += '[Date non disponible]';
    }
    
    if (user?.lieu_naissance || user?.lieu_naissance_arabe) {
      birthText += ` à ${user.lieu_naissance || user.lieu_naissance_arabe} ( MAROC )`;
    } else {
      birthText += ' ( MAROC )';
    }
    
    doc.text(birthText, 20, 126);

    // Enrollment statement
    const formattedAcademicYear = `${year}/${parseInt(year) + 1}`;
    doc.text('est régulièrement inscrit(e) à la FACULTÉ DES SCIENCES JURIDIQUES ET POLITIQUES -', 20, 138);
    doc.text(`SETTAT pour l'année universitaire ${formattedAcademicYear}.`, 20, 146);

    // Program information
    let programY = 158;
    if (registrations && registrations.length > 0) {
      const mainRegistration = registrations[0];
      
      if (mainRegistration.lib_dip) {
        doc.text(`Diplôme : ${mainRegistration.lib_dip}`, 20, programY);
        programY += 8;
      } else if (mainRegistration.lib_etp) {
        doc.text(`Diplôme : ${mainRegistration.lib_etp}`, 20, programY);
        programY += 8;
      }
      
      if (mainRegistration.lib_etp || mainRegistration.lic_etp) {
        doc.text(`Année : ${mainRegistration.lib_etp || mainRegistration.lic_etp}`, 20, programY);
      }
    }

    // ===== GENERATE SECURITY FEATURES - MATCH OfficialDocuments EXACTLY =====
    const documentData = {
      studentId: user?.cod_etu,
      semester: `YEAR_${year}`,  // ← Changed to match the format used in storage
      timestamp: new Date().toISOString(),
      registrations: registrations.map(r => ({
        cod_etp: r.cod_etp,
        lib_etp: r.lib_etp
      }))
    };

    const { signature, qrCodeDataUrl } = await DocumentSecurity.signDocument(documentData);

    // ===== FOOTER SECTION =====
    const footerStartY = 180;

    // Date and Dean info (RIGHT)
    const currentDate = new Date();
    const dateText = `Fait à SETTAT, le ${currentDate.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })}`;
    doc.setFontSize(10);
    doc.text(dateText, pageWidth - 20, footerStartY, { align: 'right' });

    doc.setFont('Amiri', 'bold');
    doc.text('La Doyenne de la FSJP - Settat', pageWidth - 20, footerStartY + 8, { align: 'right' });
    doc.setFont('Amiri', 'normal');

    doc.setFontSize(9);
    doc.text(user?.cod_etu || '', pageWidth - 20, footerStartY + 16, { align: 'right' });

    // ===== QR CODE (LEFT) =====
    const qrX = 15;
    const qrY = footerStartY - 5;
    const qrSize = 35;

    try {
      if (qrCodeDataUrl) {
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.5);
        doc.rect(qrX - 1, qrY - 1, qrSize + 2, qrSize + 2);
        
        const qrCodeImage = qrCodeDataUrl.split(',')[1];
        doc.addImage(qrCodeImage, 'PNG', qrX, qrY, qrSize, qrSize);
        
        doc.setFontSize(7);
        doc.text('Code QR de Vérification', qrX + (qrSize / 2), qrY + qrSize + 4, { align: 'center' });
        doc.text('Scanner pour vérifier', qrX + (qrSize / 2), qrY + qrSize + 8, { align: 'center' });
      }
    } catch (qrError) {
      console.warn('QR code generation failed:', qrError);
    }

    // ===== DIGITAL SIGNATURE BOX (CENTER) =====
    const sigX = 60;
    const sigY = footerStartY + 10;
    const sigWidth = 90;
    const sigHeight = 18;

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.rect(sigX, sigY, sigWidth, sigHeight);

    doc.setFontSize(7);
    doc.setFont('Amiri', 'bold');

    doc.text('Signature Numérique', sigX + (sigWidth / 2), sigY + 8, { align: 'center' });

    if (signature && signature.length > 0) {
      doc.setFont('Courier', 'normal');
      doc.setFontSize(6);
      doc.text(signature, sigX + (sigWidth / 2), sigY + 13, { align: 'center' });
    }



    // ===== UNIVERSITY FOOTER =====
    const bottomY = pageHeight - 20;
    
    doc.setFontSize(8);
    doc.setFont('Amiri', 'normal');
    
    doc.text('Adresse : COMPLEXE UNIVERSITAIRE - SETTAT', 15, bottomY);
    doc.text('Tél. : 0523721939  |  FAX : 0523724087', 15, bottomY + 5);

    doc.text('العنوان : المركب الجامعي - سطات', pageWidth - 15, bottomY, { align: 'right' });
    doc.text('الهاتف : 0523721939  |  الفاكس : 0523724087', pageWidth - 15, bottomY + 5, { align: 'right' });

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text('16005333', pageWidth - 15, bottomY + 10, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    return doc;
  } catch (error) {
    console.error('Certificate generation error:', error);
    throw error;
  }
};

  const handleDownloadCertificate = async (year, registrations) => {
    setIsGenerating(true);
    setError(null);
    try {
      const doc = await generateEnrollmentCertificate(year, registrations);
      if (doc) {
        doc.save(`Certificat_Scolarite_${year}_${user?.cod_etu}.pdf`);
        studentAPI.logDocumentDownload('certificat_scolarite', `YEAR_${year}`).catch(() => {});
      }
    } catch (err) {
      console.error("Error generating enrollment certificate:", err);
      setError('Une erreur s\'est produite lors de la génération du certificat.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return <Loading message={t('as_loading')} />;
  }

  const sortedYears = situationData
    ? Object.keys(situationData).sort((a, b) => parseInt(b) - parseInt(a))
    : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Page Header ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
          borderRadius: 4,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 52, height: 52, borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <AdminIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              {t('as_title')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
              {t('as_subtitle')}
            </Typography>
          </Box>
        </Box>

        <Button
          startIcon={<RefreshIcon />}
          onClick={() => fetchAdministrativeSituation(selectedYear)}
          size="small"
          sx={{
            color: 'white',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 2,
            px: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {t('as_refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Year Filter ── */}
      {availableYears.length > 1 && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('as_yearLabel')}
          </Typography>
          <Chip
            label={t('as_allYears')}
            onClick={() => handleYearChange({ target: { value: '' } })}
            variant={selectedYear === '' ? 'filled' : 'outlined'}
            color={selectedYear === '' ? 'primary' : 'default'}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          />
          {availableYears.map((y) => (
            <Chip
              key={y}
              label={`${y} / ${parseInt(y) + 1}`}
              onClick={() => handleYearChange({ target: { value: y.toString() } })}
              variant={selectedYear === y.toString() ? 'filled' : 'outlined'}
              color={selectedYear === y.toString() ? 'primary' : 'default'}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {/* ── Year Cards ── */}
      {sortedYears.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {sortedYears.map((year, yearIdx) => {
            const registrations = situationData[year].sort(
              (a, b) => new Date(b.dat_cre_iae) - new Date(a.dat_cre_iae)
            );
            const isLatest = yearIdx === 0;

            return (
              <Paper
                key={year}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: isLatest ? '1.5px solid #3498db' : '1.5px solid #e8ecef',
                  overflow: 'hidden',
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    px: 3, py: 2,
                    bgcolor: isLatest ? '#eaf4fd' : '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    borderBottom: '1px solid',
                    borderColor: isLatest ? '#bee3f8' : '#e8ecef',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalendarIcon sx={{ color: isLatest ? '#3498db' : '#95a5a6', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={isLatest ? '#1a6fa8' : 'text.primary'}>
                      {t('as_academicYear')} {year} / {parseInt(year) + 1}
                    </Typography>
                    {isLatest && (
                      <Chip label={t('as_currentYear')} size="small" color="primary" sx={{ borderRadius: 1.5, fontWeight: 600, height: 22 }} />
                    )}
                    <Chip
                      label={t('as_nReg', { count: registrations.length })}
                      size="small"
                      sx={{ borderRadius: 1.5, bgcolor: isLatest ? '#d0eafc' : '#ececec', fontWeight: 600, height: 22 }}
                    />
                  </Box>

                  <Button
                    startIcon={<PdfIcon />}
                    disabled
                    size="small"
                    variant={isLatest ? 'contained' : 'outlined'}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: '0.78rem' }}
                  >
                    {t('as_certificate')}
                  </Button>
                </Box>

                {/* Registrations */}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {registrations.map((reg, idx) => {
                    const isActive = reg.eta_iae === 'E';
                    return (
                      <Box
                        key={`${reg.cod_etp}-${idx}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                          px: 2, py: 1.5,
                          borderRadius: 2.5,
                          bgcolor: isActive ? '#f0fdf4' : '#fafafa',
                          border: '1px solid',
                          borderColor: isActive ? '#bbf7d0' : '#ececec',
                        }}
                      >
                        {/* Left: name + diploma */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 38, height: 38, borderRadius: 2, flexShrink: 0,
                              bgcolor: isActive ? '#dcfce7' : '#f1f5f9',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <SchoolIcon sx={{ fontSize: 20, color: isActive ? '#16a34a' : '#94a3b8' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                              {reg.lib_etp || reg.lic_etp || t('as_unknown')}
                            </Typography>
                            {reg.lib_dip && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                                {reg.lib_dip}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Right: badges + date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          {reg.tem_iae_prm === 'O' && (
                            <Chip
                              label={t('as_primary')}
                              size="small"
                              sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, height: 22, borderRadius: 1.5 }}
                            />
                          )}
                          <Chip
                            icon={isActive
                              ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                              : <CancelIcon sx={{ fontSize: '14px !important' }} />
                            }
                            label={isActive ? t('as_active') : t('as_inactive')}
                            size="small"
                            sx={{
                              height: 24, borderRadius: 1.5, fontWeight: 700,
                              bgcolor: isActive ? '#dcfce7' : '#fee2e2',
                              color: isActive ? '#15803d' : '#dc2626',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                          {reg.dat_cre_iae && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {formatDate(reg.dat_cre_iae)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}
        >
          <SchoolIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            {t('as_noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedYear
              ? t('as_noRegYear', { year: selectedYear })
              : t('as_noRegAll')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AdministrativeSituation;